import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, ShoppingCart, Boxes, AlertTriangle, PackageCheck, BadgeCheck, Truck, TrendingDown, ArrowRight, Check } from "lucide-react";
import { api, rp, dt } from "@/lib/api";
import { PageHeader, Stat, Card, Table, StatusBadge, Money } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { markDone } from "@/pages/Reminders";

const SEV = { danger: "border-l-rose-500 bg-rose-50/50", warning: "border-l-amber-500 bg-amber-50/40", info: "border-l-sky-500 bg-sky-50/40" };

const Urgent = ({ items, className = "", reload }) => (
  <Card title="Tindakan mendesak" className={className} testid="urgent-actions">
    <div className="p-3 space-y-1.5 max-h-[420px] overflow-y-auto">
      {items.length === 0 && <div className="text-sm text-slate-500 p-3">Tidak ada yang mendesak.</div>}
      {items.map((a, i) => (
        <div key={i} className="flex items-center gap-2">
          <Link to={a.link} data-testid={`urgent-item-${i}`} className={`flex-1 flex items-center justify-between gap-3 border-l-4 rounded-r-md px-3 py-2 text-[13px] hover:translate-x-0.5 transition-transform ${SEV[a.severity]}`}>
            <span className="font-medium text-slate-800">{a.title}</span><ArrowRight size={14} className="text-slate-400 shrink-0" />
          </Link>
          {a.type === "reminder" && <button onClick={() => markDone(a.reminder_id, reload)} title="Tandai selesai" data-testid={`urgent-reminder-done-${i}`}
            className="h-8 px-2.5 inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 shrink-0"><Check size={13} /> Selesai</button>}
        </div>
      ))}
    </div>
  </Card>
);

const OutletDashboard = ({ d, name, reload }) => (
  <div data-testid="outlet-dashboard">
    <PageHeader title={`Halo, ${name}`} sub="Apa yang perlu dikerjakan di outlet hari ini?" />
    <div className="grid grid-cols-2 gap-3 max-w-xl">
      <Stat testid="card-low-stock" label="Stok menipis" value={d.cards.low_stock} icon={AlertTriangle} tone="rose" to="/inventory/outlets" />
      <Stat testid="card-pending-receiving" label="Menunggu penerimaan" value={d.cards.pending_receiving} icon={PackageCheck} tone="amber" to="/receiving" />
    </div>
    <Urgent items={d.attention} className="mt-4 max-w-3xl" reload={reload} />
  </div>
);

const OpsDashboard = ({ d, name, reload }) => (
  <div data-testid="ops-dashboard">
    <PageHeader title={`Halo, ${name}`} sub="Apa yang perlu perhatian Anda hari ini?" />
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Stat testid="card-low-stock" label="Stok menipis" value={d.cards.low_stock} icon={AlertTriangle} tone="rose" to="/inventory/warehouse" />
      <Stat testid="card-pending-receiving" label="Menunggu penerimaan" value={d.cards.pending_receiving} icon={PackageCheck} tone="amber" to="/receiving" />
      <Stat testid="card-pending-delivery" label="Pengiriman tertunda" value={d.cards.pending_delivery} icon={Truck} tone="indigo" to="/delivery-requests" />
    </div>
    <div className="grid xl:grid-cols-2 gap-4 mt-4">
      <Urgent items={d.attention} reload={reload} />
      <Card title="Transaksi terbaru" testid="recent-transactions">
        <Table rows={d.recent} cols={[
          { h: "Kode", r: (r) => <Link to={r.link} className="num text-xs font-semibold text-emerald-700 hover:underline">{r.code}</Link> },
          { h: "Jenis", k: "type" },
          { h: "Rute", r: (r) => <span className="text-xs">{r.from}{r.to !== r.from ? ` → ${r.to}` : ""}</span> },
          { h: "Status", r: (r) => <StatusBadge s={r.status} /> },
          { h: "Waktu", r: (r) => <span className="text-xs text-slate-500">{dt(r.created_at)}</span> },
        ]} />
      </Card>
    </div>
  </div>
);

export default function Dashboard() {
  const { user, can } = useAuth();
  const [d, setD] = useState(null);
  const load = () => api.get("/dashboard").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="text-sm text-slate-500">Memuat dashboard…</div>;
  if (!d.overview) return <OutletDashboard d={d} name={user.name.split(" ")[0]} reload={load} />;
  if (!d.money) return <OpsDashboard d={d} name={user.name.split(" ")[0]} reload={load} />;
  const c = d.cards;
  const f = d.finance;
  return (
    <div>
      <PageHeader title={`Halo, ${user.name.split(" ")[0]}`} sub="Apa yang perlu perhatian Anda hari ini?" />
      <div className="grid grid-cols-2 md:grid-cols-4 2xl:grid-cols-8 gap-3">
        <Stat testid="card-cash-balance" label="Saldo kas saat ini" value={rp(c.cash_balance)} icon={Wallet} tone="emerald" to="/cash" />
        <Stat testid="card-purchases-today" label="Pembelian hari ini" value={rp(c.purchases_today)} icon={ShoppingCart} tone="indigo" to="/purchases" />
        <Stat testid="card-inventory-value" label="Nilai inventaris" value={rp(c.inventory_value)} icon={Boxes} to="/reports/inventory" />
        <Stat testid="card-low-stock" label="Stok menipis" value={c.low_stock} icon={AlertTriangle} tone="rose" to="/inventory/outlets" />
        <Stat testid="card-pending-receiving" label="Menunggu penerimaan" value={c.pending_receiving} icon={PackageCheck} tone="amber" to="/receiving" />
        {can("purchase.verify", "transfer.verify") && <Stat testid="card-pending-review" label="Perlu review" value={c.pending_review} icon={BadgeCheck} tone="sky" to="/review" />}
        <Stat testid="card-pending-delivery" label="Pengiriman tertunda" value={c.pending_delivery} icon={Truck} tone="indigo" to="/delivery-requests" />
        <Stat testid="card-variance" label="Variansi stok (MTD)" value={rp(c.variance_mtd)} icon={TrendingDown} tone="rose" to="/inventory/variance" />
      </div>

      <div className="grid xl:grid-cols-3 gap-4 mt-4">
        <Urgent items={d.attention} className="xl:col-span-2" reload={load} />
        <Card title="Keuangan" testid="finance-snapshot">
          <div className="grid grid-cols-2 divide-x">
            {[["Hari ini", [["Penjualan tunai", f.today.cash_sales], ["Setoran bank", f.today.deposits], ["Biaya outlet", f.today.expenses], ["Pembelian", f.today.purchases], ["Bayar hutang", f.today.debt_paid], ["Mutasi stok", f.today.inventory_movement]]],
              ["Bulan ini", [["Penjualan tunai", f.mtd.cash_sales], ["Setoran bank", f.mtd.deposits], ["Top-up Owner", f.mtd.owner_topup], ["Biaya outlet", f.mtd.opex], ["Pembelian", f.mtd.purchases], ["Sisa hutang supplier", f.mtd.payables]]]].map(([t, rows]) => (
              <div key={t} className="p-3">
                <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-2">{t}</div>
                {rows.map(([k, v]) => <div key={k} className="flex justify-between text-[12px] py-0.5"><span className="text-slate-500">{k}</span><Money v={v} className="font-semibold text-slate-800" /></div>)}
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid xl:grid-cols-5 gap-4 mt-4">
        <Card title="Transaksi terbaru" className="xl:col-span-3" testid="recent-transactions">
          <Table rows={d.recent} cols={[
            { h: "Kode", r: (r) => <Link to={r.link} className="num text-xs font-semibold text-emerald-700 hover:underline">{r.code}</Link> },
            { h: "Jenis", k: "type" },
            { h: "Rute", r: (r) => <span className="text-xs">{r.from}{r.to !== r.from ? ` → ${r.to}` : ""}</span> },
            { h: "Status", r: (r) => <StatusBadge s={r.status} /> },
            { h: "Nilai", right: true, r: (r) => <Money v={r.value} /> },
            { h: "Waktu", r: (r) => <span className="text-xs text-slate-500">{dt(r.created_at)}</span> },
          ]} />
        </Card>
        <Card title="Ringkasan outlet (bulan ini)" className="xl:col-span-2" testid="outlet-overview">
          <Table rows={d.outlets} cols={[
            { h: "Outlet", r: (r) => <Link to={`/inventory/stock/${r.location_id}`} className="font-semibold hover:text-emerald-700">{r.name}</Link> },
            { h: "Kas outlet", right: true, r: (r) => <Money v={r.cash} /> },
            { h: "Nilai stok", right: true, r: (r) => <Money v={r.inventory_value} /> },
            { h: "Menipis", right: true, r: (r) => <span className={r.low_stock ? "text-rose-600 font-semibold" : ""}>{r.low_stock}</span> },
            { h: "Variansi", right: true, r: (r) => <Money v={r.variance} /> },
            { h: "Terima", right: true, k: "pending_receiving" },
          ]} />
        </Card>
      </div>
    </div>
  );
}
